// src/utils/SmartImageEnhancer.js
// Upgrades existing <img> tags:
//  - precise sizes + JPEG srcset on <img> fallback
//  - <picture> with AVIF/WebP sources when manifest lists them
//  - never overrides original img.src (prevents hashed-import breakage)
//  - auto LCP priority, optional aggressive deferral, mutation/resize aware

const DEFAULT_SIZES =
  '(max-width: 600px) 100vw, (max-width: 1024px) 92vw, 1200px';

const DEFAULT_WIDTHS = [480, 768, 1200, 1600];
const AGGRESSIVE_ROOT_MARGIN = '600px 0px';

// ---------------- Utilities ----------------

function fileNameFromUrl(src = '') {
  try {
    const clean = src.split('#')[0].split('?')[0];
    return clean.split('/').pop() || '';
  } catch {
    return '';
  }
}

// "hero.abcd123.png" -> "hero", "hero-1200.jpg" -> "hero-1200"
function roughBaseFromSrc(src = '') {
  const file = fileNameFromUrl(src);
  if (!file) return null;
  const noExt = file.replace(/\.(avif|webp|jpe?g|png|svg)$/i, '');
  const noHash = noExt.replace(/\.[a-f0-9]{6,}/i, '');
  return noHash || null;
}

// Safer resolver: prefer explicit, exact, and "-width" trimmed. No risky fuzzy override.
function resolveBaseSafe(rough, manifest) {
  if (!rough || !manifest) return null;
  if (manifest[rough]) return rough;
  const trimmed = rough.replace(/-\d{2,4}$/, '');
  if (trimmed && manifest[trimmed]) return trimmed;
  return null; // require explicit data-smart-base for hashed imports
}

function closestWidth(target, candidates) {
  const sorted = [...candidates].sort((a, b) => a - b);
  for (const w of sorted) if (w >= target) return w;
  return sorted[sorted.length - 1] || sorted[0] || 1200;
}

function buildSrcSet(base, widths, ext, publicBase) {
  const baseUrl = publicBase || '';
  return widths.map((w) => `${baseUrl}/img/optimized/${base}-${w}.${ext} ${w}w`).join(', ');
}

function setIfMissing(el, attr, value) {
  if (!el.getAttribute(attr) && value != null) el.setAttribute(attr, value);
}

function deferUntilVisible(el, fn, rootMargin = AGGRESSIVE_ROOT_MARGIN) {
  if (!('IntersectionObserver' in window)) { fn(); return; }
  const io = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect(); fn();
    },
    { rootMargin }
  );
  io.observe(el);
}

function markLargestInViewAsPriority() {
  try {
    const vpH = window.innerHeight || 800;
    let best = null, bestScore = 0;
    document.querySelectorAll('img').forEach((img) => {
      if (img.getAttribute('fetchpriority') === 'high') return;
      const r = img.getBoundingClientRect?.();
      if (!r) return;
      if (r.bottom <= 0 || r.top >= vpH * 1.2) return;
      const visibleH = Math.max(0, Math.min(r.bottom, vpH) - Math.max(0, r.top));
      const score = visibleH * Math.max(1, Math.min(r.width, window.innerWidth));
      if (score > bestScore) { bestScore = score; best = img; }
    });
    if (best) {
      best.setAttribute('fetchpriority', 'high');
      best.removeAttribute('loading'); // LCP should not be lazy
    }
  } catch {}
}

// -------------- Multi-MIME wrapper --------------

function upgradeToPicture(img, base, widths, publicBase, formats) {
  const fmtSet = Array.isArray(formats)
    ? new Set(formats.map((f) => String(f).toLowerCase()))
    : (formats && typeof formats === 'object')
      ? new Set(Object.keys(formats).filter((k) => formats[k]))
      : new Set();

  const hasAvif = fmtSet.has('avif');
  const hasWebp = fmtSet.has('webp');

  if (!hasAvif && !hasWebp) return;
  if (img.parentElement && img.parentElement.tagName === 'PICTURE') return;

  const pic = document.createElement('picture');

  // Optional: mirror identity for existing selectors; also add a common class hook
  if (img.className) pic.className = img.className;
  pic.classList.add('smart-picture');
  pic.classList.add('smart-picture');

  const sizes = img.getAttribute('sizes') || DEFAULT_SIZES;

  if (hasAvif) {
    const s = document.createElement('source');
    s.type = 'image/avif';
    s.setAttribute('srcset', buildSrcSet(base, widths, 'avif', publicBase));
    s.setAttribute('sizes', sizes);
    pic.appendChild(s);
  }
  if (hasWebp) {
    const s = document.createElement('source');
    s.type = 'image/webp';
    s.setAttribute('srcset', buildSrcSet(base, widths, 'webp', publicBase));
    s.setAttribute('sizes', sizes);
    pic.appendChild(s);
  }

  // Insert before, then move the <img> inside as JPEG/PNG fallback
  img.parentNode.insertBefore(pic, img);
  pic.appendChild(img);
}

// -------------- Core enhancement --------------

function enhanceOne(img, manifest, defaultSizes, publicBase, devLog) {
  if (img.dataset.smartUpgraded === '1' || img.hasAttribute('data-smart-done')) return;
  if (img.dataset.smartSkip === '1') return;

  // Explicit mapping wins (handles hashed imports reliably)
  const explicitBase = img.getAttribute('data-smart-base');

  // Otherwise try safe inference
  const rough = explicitBase || roughBaseFromSrc(img.getAttribute('src') || '');
  const resolvedBase = explicitBase || resolveBaseSafe(rough, manifest);

  const entry = resolvedBase && manifest[resolvedBase];
  if (!entry) {
    if (devLog) console.debug('[SmartImage] no manifest match for', fileNameFromUrl(img.src || ''), 'rough=', rough);
    return; // leave as-is (no breakage)
  }

  const widths = Array.isArray(entry.widths) && entry.widths.length ? entry.widths : DEFAULT_WIDTHS;
  const formats = entry.formats || []; // ["avif","webp","jpg"] or object map

  const applyEnhancements = () => {
    // 1) JPEG srcset on <img> fallback (keep original img.src intact)
    const jpgSrcset = buildSrcSet(resolvedBase, widths, 'jpg', publicBase);
    if (jpgSrcset) img.setAttribute('srcset', jpgSrcset);

    // 2) Precise sizes via measurement (improves choice & bytes)
    const measure = () => {
      try {
        const cw = Math.ceil(img.clientWidth || img.parentElement?.clientWidth || 0);
        if (cw > 0) {
          img.setAttribute('sizes', `${cw}px`);
        } else {
          if (!img.getAttribute('sizes')) img.setAttribute('sizes', defaultSizes);
        }
      } catch {
        if (!img.getAttribute('sizes')) img.setAttribute('sizes', defaultSizes);
      }
    };
    measure();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => measure());
      ro.observe(img);
      img.dataset.smartRo = '1';
    } else {
      const onResize = () => measure();
      window.addEventListener('resize', onResize);
      img.dataset.smartResizeBound = '1';
    }

    // 3) Priority / lazy
    const eagerFlag = img.getAttribute('data-smart-eager') === '1';
    if (img.getAttribute('data-priority') === 'high') {
      img.setAttribute('fetchpriority', 'high');
      img.removeAttribute('loading');
    } else if (eagerFlag) {
      img.setAttribute('loading', 'eager');
    } else {
      setIfMissing(img, 'loading', 'lazy');
    }
    setIfMissing(img, 'decoding', 'async');

    // 4) Add AVIF/WebP via <picture> (fallback remains original img.src)
    upgradeToPicture(img, resolvedBase, widths, publicBase, formats);

    img.dataset.smartUpgraded = '1';
    img.setAttribute('data-smart-done', '1');
    if (devLog) console.debug('[SmartImage] upgraded', resolvedBase, '→', img);
  };

  // Optional aggressive defer (opt-in per <img>)
  if (img.hasAttribute('data-smart-aggressive')) {
    deferUntilVisible(img, applyEnhancements);
  } else {
    applyEnhancements();
  }
}

export async function enhanceSmartImages(options = {}) {
  const {
    selector = 'img',
    sizes = DEFAULT_SIZES,
    observe = true,
    devLog = false,
  } = options;

  const publicBase =
    (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';

  // Load manifest
  let manifest = {};
  try {
    const res = await fetch(`${publicBase}/img-manifest.json`, { cache: 'no-store' });
    if (res.ok) {
      manifest = await res.json();
      if (devLog) console.debug('[SmartImage] manifest loaded:', manifest);
    } else {
      console.warn('SmartImage: manifest fetch returned', res.status);
    }
  } catch (e) {
    console.warn('SmartImage: manifest fetch failed; passthrough mode', e);
    manifest = {};
  }

  // First pass
  document.querySelectorAll(selector).forEach((img) =>
    enhanceOne(img, manifest, sizes, publicBase, devLog)
  );

  // Promote likely LCP to high priority (runs before paint if you call via useLayoutEffect)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markLargestInViewAsPriority, { once: true });
  } else {
    markLargestInViewAsPriority();
  }

  if (!observe) return;

  // Watch for images added later
  if ('MutationObserver' in window) {
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') {
            enhanceOne(node, manifest, sizes, publicBase, devLog);
          } else {
            node.querySelectorAll?.('img').forEach((img) =>
              enhanceOne(img, manifest, sizes, publicBase, devLog)
            );
          }
        });
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
}

export default { enhanceSmartImages };
